import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { listUsers, type ListUsersQuery } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';

type UsersResponse = NonNullable<Awaited<ReturnType<typeof listUsers>>['data']>;

function emptyUsersResponse(pageSize: number): UsersResponse {
  return {
    items: [],
    meta: { filteredTotal: 0, page: 1, pageCount: 1, pageSize, total: 0 },
    summary: {
      activeUsers: 0,
      adminUsers: 0,
      disabledUsers: 0,
      pendingVerificationUsers: 0,
      totalUsers: 0,
      verifiedUsers: 0,
    },
  };
}

export function useAdminUsers(administratorId: string, filters: ListUsersQuery) {
  const queryClient = useQueryClient();
  const listKey = ['admin-users', administratorId] as const;
  const query = useQuery({
    queryKey: [...listKey, filters],
    queryFn: async () => {
      const result = await listUsers(filters);
      if (result.error) throw result.error;
      return result.data ?? emptyUsersResponse(filters.pageSize ?? 10);
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (query.error) toast.error(getAuthErrorMessage(query.error));
  }, [query.error, query.errorUpdatedAt]);

  return {
    data: query.data ?? emptyUsersResponse(filters.pageSize ?? 10),
    isLoading: query.isPending,
    isRefreshing: query.isRefetching,
    // Resolve the active filters when a mutation finishes, even if its handler
    // captured an earlier page. Late responses stay in their own query cache.
    refresh: () => queryClient.invalidateQueries({ queryKey: listKey }),
  };
}
